import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

export interface FrontMessageAuthor {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
}

export interface FrontMessageRecipient {
  name: string | null;
  handle: string | null;
  role: string | null;
}

export interface FrontMessageAttachment {
  id: string;
  filename: string;
  url: string;
  content_type: string;
  size: number;
}

export interface FrontMessageDetail {
  id: string;
  conversation_id: string | null;
  subject: string | null;
  body: string | null;
  text: string | null;
  blurb: string | null;
  created_at: string | null;
  is_inbound: boolean;
  author: FrontMessageAuthor | null;
  recipients: FrontMessageRecipient[];
  attachments: FrontMessageAttachment[];
}

export interface FrontConversationDetail {
  id: string;
  subject: string | null;
  status: string | null;
  created_at: string | null;
  assignee: FrontMessageAuthor | null;
  recipient: {
    name: string | null;
    handle: string | null;
  } | null;
}

export interface FrontConversationResponse {
  conversation: FrontConversationDetail;
  messages: FrontMessageDetail[];
}

export const frontMessageApi = createApi({
  reducerPath: 'frontMessageApi',
  tagTypes: ['FrontMessage', 'FrontConversation'],
  baseQuery: baseQueryWithErrorHandling,
  endpoints: (builder) => ({
    // Get a single Front message by ID
    getFrontMessage: builder.query<
      FrontMessageDetail,
      { matterId: string; messageId: string }
    >({
      query: ({ matterId, messageId }) =>
        `api/matters/${matterId}/front/messages/${messageId}/`,
      providesTags: (result, error, { messageId }) => [
        { type: 'FrontMessage', id: messageId },
      ],
    }),

    // Get full conversation with all messages by conversation ID (Front's cnv_xxx ID)
    getFrontConversation: builder.query<
      FrontConversationResponse,
      { matterId: string; conversationId: string }
    >({
      query: ({ matterId, conversationId }) =>
        `api/matters/${matterId}/front/conversations/${conversationId}/`,
      providesTags: (result, error, { conversationId }) => [
        { type: 'FrontConversation', id: conversationId },
      ],
    }),

    // Get FrontConversation from DB by UUID and fetch its messages from Front
    getFrontConversationByDbId: builder.query<
      FrontConversationResponse,
      { matterId: string; conversationDbId: string }
    >({
      query: ({ matterId, conversationDbId }) =>
        `api/matters/${matterId}/front/conversation-by-db-id/${conversationDbId}/`,
      providesTags: (result, error, { conversationDbId }) => [
        { type: 'FrontConversation', id: conversationDbId },
      ],
    }),

    // Send a reply to a Front conversation (uses the same send-email endpoint)
    sendFrontReply: builder.mutation<
      { conversation_id?: string; message_id?: string },
      {
        matterId: string;
        conversationId: string; // Front's cnv_xxx ID
        conversationDbId: string; // Database UUID for cache invalidation
        subject: string;
        body: string;
        attachments?: Array<{ filename: string; base64: string }>;
      }
    >({
      query: ({
        matterId,
        conversationId,
        subject,
        body,
        attachments = [],
      }) => ({
        url: `api/matters/${matterId}/front/send-email/`,
        method: 'POST',
        body: {
          to: [],
          subject,
          body,
          channel_id: null,
          conversation_id: conversationId,
          attachments,
        },
      }),
      // Invalidate by DB ID to match getFrontConversationByDbId query
      invalidatesTags: (
        result,
        error,
        { conversationDbId, conversationId },
      ) => [
        { type: 'FrontConversation', id: conversationDbId },
        { type: 'FrontConversation', id: conversationId },
      ],
    }),
  }),
});

export const {
  useGetFrontMessageQuery,
  useLazyGetFrontMessageQuery,
  useGetFrontConversationQuery,
  useLazyGetFrontConversationQuery,
  useGetFrontConversationByDbIdQuery,
  useLazyGetFrontConversationByDbIdQuery,
  useSendFrontReplyMutation,
} = frontMessageApi;
